import { useEffect, useRef } from "react";

let pendingSentinelCleanup = null;
let nextSentinelId = 0;

function sheetSentinelId() {
  nextSentinelId += 1;
  return `hm-sheet-${nextSentinelId}`;
}

function isMobileContext() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return (
    window.matchMedia("(max-width: 768px)").matches ||
    window.matchMedia("(min-width: 769px) and (max-width: 1024px) and (orientation: portrait)").matches
  );
}

/**
 * Intercepts the mobile hardware back button (and browser back gesture)
 * to close a sheet/modal instead of navigating away.
 *
 * When `enabled` becomes true the hook pushes a sentinel history entry.
 * - Back button pressed  → popstate fires → `onClose()` called, sentinel consumed.
 * - Normal close (X / scrim tap) → component unmounts → sentinel popped silently.
 *
 * On desktop (> 768px landscape) the hook is a no-op so browser back
 * navigates normally.
 */
export default function useBackButton(onClose, enabled = true) {
  const callbackRef = useRef(onClose);
  useEffect(() => { callbackRef.current = onClose; });

  useEffect(() => {
    if (!enabled) return;
    // No-op on desktop — let the browser handle back navigation normally.
    if (!isMobileContext()) return;

    // React Strict Mode deliberately runs mount -> cleanup -> mount again in
    // development. Defer normal-close cleanup by one task so the second mount
    // can reclaim the existing sentinel instead of receiving a stale popstate
    // and immediately closing the newly opened sheet.
    const existingSentinelId = window.history.state?.hmSheetId;
    if (
      pendingSentinelCleanup &&
      existingSentinelId === pendingSentinelCleanup.id
    ) {
      window.clearTimeout(pendingSentinelCleanup.timer);
      pendingSentinelCleanup = null;
    }

    const sentinelId = existingSentinelId || sheetSentinelId();
    if (!existingSentinelId) {
      window.history.pushState(
        { ...(window.history.state || {}), hmSheet: true, hmSheetId: sentinelId },
        "",
      );
    }

    let consumedByBack = false;

    function onPopState() {
      consumedByBack = true;
      callbackRef.current();
    }

    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("popstate", onPopState);
      // If the sheet was closed normally (not via back button),
      // silently consume the sentinel entry we pushed. The deferred cleanup
      // is cancelled if another sheet mounts synchronously (including the
      // second Strict Mode effect pass).
      if (!consumedByBack && window.history.state?.hmSheetId === sentinelId) {
        const cleanup = {
          id: sentinelId,
          timer: window.setTimeout(() => {
            if (window.history.state?.hmSheetId === sentinelId) {
              window.history.back();
            }
            if (pendingSentinelCleanup === cleanup) {
              pendingSentinelCleanup = null;
            }
          }, 50),
        };
        pendingSentinelCleanup = cleanup;
      }
    };
  }, [enabled]);
}
